<script setup lang="ts">
import { EButton, EDialog, EField, EInput, formatDate, useToast } from '@erpx/ui'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { stepUp } from '@/api/stepup'

/**
 * The one place a step-up challenge is answered in the platform console
 * (CLAUDE.md §6/§13).
 *
 * Mounted at the root, it appears whenever any request is refused with
 * `ERR_STEP_UP_REQUIRED`, prompts for a one-time code, and — once the code is
 * accepted — the transport replays the request the operator actually made. An
 * operator halfway through a support action does not lose it to a refusal.
 *
 * MFA is mandatory in this realm, so there is no "not enrolled yet" state to
 * design for on the way in; the server can still answer `ERR_MFA_REQUIRED` (an
 * account mid-enrolment, or a token with no session behind it), and that is
 * routed to the enrolment screen rather than re-prompted, because another code
 * would be refused for exactly the same reason.
 *
 * Accessibility (§11): `EDialog` traps focus, labels itself and restores focus
 * on close; the code field takes focus on open and failures are announced.
 */

const router = useRouter()
const toast = useToast()
const { t, locale } = useI18n()

const prompt = stepUp.prompt
const code = ref('')
const codeInput = ref<InstanceType<typeof EInput> | null>(null)
/** The last value submitted, so auto-submit does not resend a refused code. */
let submitted = ''

const open = computed(() => prompt.value !== null)

const reason = computed(() => t(`stepUp.reasons.${prompt.value?.reason ?? 'generic'}`))
const waiting = computed(() => prompt.value?.waiting ?? 0)

/**
 * This realm answers an exhausted lockout budget with 429 and an RFC 3339
 * instant in the detail. Rendered on the operator's clock it becomes an answer
 * to "when can I try again" rather than a machine timestamp.
 */
const lockedUntil = computed(() => {
  const match = /\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})/.exec(prompt.value?.detail ?? '')
  if (match === null) return ''
  return formatDate(match[0], { preset: 'time', locale: locale.value })
})

const failureText = computed(() => {
  const failure = prompt.value?.failure
  if (failure === undefined || failure === null) return ''
  if (failure === 'lockout') {
    return lockedUntil.value === ''
      ? t('stepUp.errors.lockout')
      : t('stepUp.errors.lockoutUntil', { time: lockedUntil.value })
  }
  if (failure === 'code') return t('stepUp.errors.code')
  const detail = prompt.value?.detail ?? ''
  return detail === '' ? t('stepUp.errors.other') : detail
})

const lockedOut = computed(() => prompt.value?.failure === 'lockout')
const busy = computed(() => prompt.value?.busy === true)
const canSubmit = computed(() => !busy.value && !lockedOut.value && code.value.length >= 6)

/** Paste-friendly: strips the spacing authenticator apps display codes with. */
function onCode(value: string): void {
  code.value = value.replace(/\D/g, '').slice(0, 6)
  if (code.value.length === 6 && code.value !== submitted && !busy.value && !lockedOut.value) {
    void submit()
  }
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return
  submitted = code.value
  await stepUp.submit(code.value)
  if (prompt.value !== null) {
    code.value = ''
    await focusCode()
  }
}

function cancel(): void {
  stepUp.cancel()
}

async function focusCode(): Promise<void> {
  await nextTick()
  // One frame after the dialog's own opening focus, which would otherwise land
  // on the footer and steal this back.
  await new Promise((resolve) => setTimeout(resolve, 0))
  codeInput.value?.focus()
}

watch(open, (isOpen) => {
  if (!isOpen) {
    code.value = ''
    submitted = ''
    return
  }
  void focusCode()
})

stepUp.setEnrolmentHandler(() => {
  toast.warning(t('stepUp.enrol.routedTitle'), t('stepUp.enrol.routedDetail'))
  void router.push({ name: 'mfa-enrol' })
})

onBeforeUnmount(() => {
  stepUp.setEnrolmentHandler(null)
})
</script>

<template>
  <EDialog
    :open="open"
    size="sm"
    persistent
    hide-close
    :title="$t('stepUp.title')"
    :description="reason"
    @close="cancel"
  >
    <div class="flex flex-col gap-3">
      <p class="text-sm text-muted">{{ $t('stepUp.description') }}</p>

      <p v-if="waiting > 1" class="text-xs text-muted" data-test="stepup-waiting">
        {{ $t('stepUp.waiting', waiting) }}
      </p>

      <EField
        :label="$t('stepUp.codeLabel')"
        :error="failureText === '' ? null : failureText"
        required
      >
        <EInput
          ref="codeInput"
          :model-value="code"
          inputmode="numeric"
          autocomplete="one-time-code"
          :maxlength="6"
          :disabled="lockedOut"
          numeric
          :aria-label="$t('stepUp.codeLabel')"
          @update:model-value="onCode"
          @enter="submit"
        />
      </EField>

      <p
        v-if="(prompt?.attempts ?? 0) > 0 && !lockedOut"
        class="text-xs text-muted"
        data-test="stepup-budget"
      >
        {{ $t('stepUp.attemptsWarning') }}
      </p>

      <!-- Announced without stealing focus: the code field keeps it. -->
      <p class="sr-only" role="status" aria-live="polite">{{ failureText }}</p>
    </div>

    <template #footer>
      <EButton variant="ghost" @click="cancel">{{ $t('common.actions.cancel') }}</EButton>
      <EButton
        variant="primary"
        :loading="busy"
        :loading-label="$t('stepUp.verifying')"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ $t('stepUp.submit') }}
      </EButton>
    </template>
  </EDialog>
</template>
